import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import { useRef } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
function CommonAnalysis() {

	const generateTemplate = function() {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet('Sheet1');
		worksheet.columns = [
			{ header: 'Dataset 1', width: 20 },
			{ header: 'Dataset 2', width: 20 },
			{ header: 'Dataset 3', width: 20 }
		];
		worksheet.getCell('A2').value = 'Enter data you want to compare in each column';
		worksheet.getCell('A3').value = 'You can add as many columns as you want';
		worksheet.getCell('A4').value = 'The top row is for column headers, for example, LPR site 1';

		workbook.xlsx.writeBuffer().then(function(data) {
			const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'CommonAnalysisTemplate.xlsx';
			a.click();
			window.URL.revokeObjectURL(url);
		});
	}

	const uploadFileClicked = function() { fileInput.current.click(); }

	function handleFileChange(e) {
		setFile(e.target.files[0]);
		setFileName(e.target.files[0].name);
		setUploadButtonState(true);
	}

	const [file, setFile] = useState();
	const [fileName, setFileName] = useState('No file selected');
	const fileInput = useRef();
	const [uploadButtonState, setUploadButtonState] = useState(false);

	async function processFileClicked() {
    trackToolAction('/commonanalysis', 'Common Analysis');
		setUploadButtonState(false);

		const reader = new FileReader();
		reader.onload = async function(e) {
			const bstr = e.target.result;
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(bstr);

			var worksheet = workbook.getWorksheet('Sheet1');
			var commonWorksheet = workbook.addWorksheet('Common Analysis');

			const columnCount = worksheet.actualColumnCount;
			const columnData = [];
			for (let i = 1; i <= columnCount; i++) {
				const column = worksheet.getColumn(i);
				columnData.push(column.values);
			}

			const data = columnData.map(column => column.slice(1));
			let commonValues = [];

			for (let i = 0; i < data.length; i++) {
				for (let j = i + 1; j < data.length; j++) {
					const common = data[i].filter(value => data[j].includes(value));
					common.forEach(value => {
						commonValues.push({ value: value, columns: [columnData[i][1], columnData[j][1]] });
					});
				}
			}

			const grouped = commonValues.reduce((acc, { value, columns }) => {
				acc[value] = (acc[value] || []).concat(columns);
				return acc;
			}, {});

			const result = Object.entries(grouped).map(([value, columns]) => ({
				value: value,
				columns: [...new Set(columns)]
			}));

			result.forEach(item => {
				item.commonCount = item.columns.length;
				item.columns = item.columns.join(', ');
			});

			result.sort((a, b) => b.commonCount - a.commonCount);

			commonWorksheet.columns = [
				{ header: 'Value', key: 'value', width: 20 },
				{ header: 'Datasets', key: 'columns', width: 20 },
				{ header: 'Common Count', key: 'commonCount', width: 20 }
			];
			commonWorksheet.addRows(result);
			commonWorksheet.eachRow(function(row) { row.font = { size: 12 }; });

			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'Generated-Common-Analysis.xlsx';
			link.click();
			setFileName("No file selected");
		};
		reader.readAsArrayBuffer(file);
	}

	return (
		<>
			<Navbar pageTitle="Common Analysis" />
			<Container maxWidth="md" sx={{ py: 4 }}>

				<Box sx={{ mb: 3 }}>
					<Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
						Common Analysis
					</Typography>
					<Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
						Find common items between datasets such as LPR data or phone calls.
					</Typography>
				</Box>

				{/* About + Template */}
				<Paper elevation={0} sx={{
					borderRadius: '14px',
					boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
					p: '26px 26px 22px', mb: 2.5,
				}}>
					<Typography sx={{ fontSize: '14px', color: '#374151', mb: 2, lineHeight: 1.6 }}>
						Upload an <strong>.xlsx</strong> file with each dataset you want to compare as a new column. Download the template below and paste your data into it.
					</Typography>
					<Button
						variant="outlined"
						onClick={generateTemplate}
						sx={{
							borderColor: '#6d28d9', color: '#6d28d9', borderRadius: '8px', fontWeight: 600,
							'&:hover': { borderColor: '#5b21b6', bgcolor: '#f5f3ff' },
						}}
					>
						Download Template
					</Button>
				</Paper>

				{/* Upload + Process */}
				<Paper elevation={0} sx={{
					borderRadius: '14px',
					boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
					p: '26px 26px 22px', mb: 2.5,
				}}>
					<Typography sx={{ fontSize: '14px', color: '#374151', mb: 2, lineHeight: 1.6 }}>
						Upload your file to run common analysis. Once processed, a new file will be downloaded with the results in the "Common Analysis" sheet.
					</Typography>

					<input
						type="file"
						ref={fileInput}
						onChange={handleFileChange}
						style={{ display: 'none' }}
					/>

					<Box
						onClick={uploadFileClicked}
						sx={{
							border: '2px dashed #cbd5e1', borderRadius: '10px',
							p: '28px 24px', textAlign: 'center', cursor: 'pointer',
							mb: 2, transition: 'all .2s',
							'&:hover': { borderColor: '#6d28d9', bgcolor: '#f5f3ff' },
						}}
					>
						<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"
							style={{ display: 'block', margin: '0 auto 10px' }}>
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
							<polyline points="17 8 12 3 7 8"/>
							<line x1="12" y1="3" x2="12" y2="15"/>
						</svg>
						<Typography sx={{ fontSize: '14px', color: '#64748b' }}>
							<strong style={{ color: '#6d28d9' }}>Click to choose a file</strong> or drag and drop
						</Typography>
						<Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '4px' }}>.xlsx files only</Typography>
					</Box>

					{uploadButtonState && (
						<Box sx={{
							display: 'flex', alignItems: 'center', gap: 1.5,
							p: '11px 16px', bgcolor: '#f5f3ff',
							border: '1px solid #ddd6fe', borderRadius: '8px', mb: 2,
						}}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth="2">
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
								<path d="M14 2v6h6"/>
							</svg>
							<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4c1d95' }}>{fileName}</Typography>
						</Box>
					)}

					<Button
						fullWidth variant="contained"
						disabled={!uploadButtonState}
						onClick={processFileClicked}
						sx={{
							p: '10px 20px', bgcolor: '#6d28d9',
							fontSize: '14px', fontWeight: 600, borderRadius: '8px',
							'&:hover': { bgcolor: '#5b21b6' },
							'&:disabled': { bgcolor: '#94a3b8' },
						}}
					>
						Process File
					</Button>
				</Paper>
			</Container>
		</>
	);
}

export default CommonAnalysis;
